import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

ChartJS.register(...registerables);

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable15 = ({ data }) => {
  const pdfRef = useRef();

  // Convert values
  const cleanedData = data.map((item) => ({
    ...item,
    y23: Number(item.year_2023),
    y24: Number(item.year_2024),
    diff: Number(item.year_2024) - Number(item.year_2023),
    pct: (
      ((Number(item.year_2024) - Number(item.year_2023)) /
        Number(item.year_2023)) *
      100
    ).toFixed(2),
  }));

  // Insights
  const maxItem = cleanedData.reduce((prev, curr) =>
    curr.diff > prev.diff ? curr : prev
  );

  const totalGrowth = cleanedData.reduce((sum, i) => sum + i.diff, 0);
  const avgGrowth = (totalGrowth / cleanedData.length).toFixed(2);

  const chartData = {
    labels: cleanedData.map((item) => item.education_label),
    datasets: [
      {
        label: "2023 Employment",
        data: cleanedData.map((item) => item.y23),
        backgroundColor: "#D8F999",
      },
      {
        label: "2024 Employment",
        data: cleanedData.map((item) => item.y24),
        backgroundColor: "#2C92B8",
      },
      {
        label: "Growth Difference",
        data: cleanedData.map((item) => item.diff),
        backgroundColor: "#999933",
        type: "line",
        borderColor: "#999933",
        borderWidth: 2,
        tension: 0.3,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Employment Growth by Education Level (2023–2024)",
        font: { size: 18, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.raw.toLocaleString("en-US")}`,
        },
      },
    },
    scales: {
      y: { title: { display: true, text: "Employment Count" } },
      x: { title: { display: true, text: "Education Level" } },
    },
  };

  // PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, { scale: 2 }).then((canvas) => {
      const pdf = new jsPDF("p", "mm", "a4");

      const width = pdf.internal.pageSize.getWidth() - 10;
      const height = (canvas.height * width) / canvas.width;

      // Header
      pdf.addImage(utahData, "PNG", 10, 5, 30, 25);
      pdf.setFontSize(22);
      pdf.text("UTahData.org", 50, 20);
      pdf.line(10, 32, 200, 32);

      // Content
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 5, 35, width, height);

      // Footer
      pdf.line(10, 260, 200, 260);
      pdf.addImage(utahData, "PNG", 20, 265, 18, 14);
      pdf.setFontSize(10);
      pdf.text("Built by", 45, 276);
      pdf.addImage(bodex, "PNG", 58, 268, 40, 10);
      pdf.text("using", 105, 276);
      pdf.addImage(kpi, "PNG", 118, 265, 18, 18);

      pdf.text(`Version: 20250916.v3.198`, 150, 276);
      pdf.text(`Exported: ${new Date().toLocaleDateString()}`, 145, 292);
      pdf.text(`Source: ${window.location.href}`, 10, 292);

      pdf.save("EngTable15.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold text-center mb-4 text-gray-800">
          Employment Growth by Education Level (2023–2024)
        </h2>

        {/* KPI Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-purple-100 shadow">
            <p className="font-semibold text-purple-900">📈 Highest Growth</p>
            <p className="text-sm text-gray-800">{maxItem.education_label}</p>
            <p className="font-bold text-purple-800">
              {maxItem.diff.toLocaleString("en-US")} Jobs
            </p>
          </div>

          <div className="p-4 rounded-xl bg-green-100 shadow">
            <p className="font-semibold text-green-900">📊 Total Growth</p>
            <p className="font-bold text-green-800">
              {totalGrowth.toLocaleString("en-US")} Jobs
            </p>
          </div>

          <div className="p-4 rounded-xl bg-blue-100 shadow">
            <p className="font-semibold text-blue-900">
              📌 Avg Growth per Category
            </p>
            <p className="font-bold text-blue-800">
              {avgGrowth.toLocaleString("en-US")} Jobs
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full text-left shadow-md border rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-3">Education Level</th>
                    <th>2023</th>
                    <th>2024</th>
                    <th>Growth</th>
                    <th>% Change</th>
                  </tr>
                </thead>
                <tbody>
                  {cleanedData.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition">
                      <td className="p-2 font-semibold">
                        {row.education_label}
                      </td>
                      <td>{row.y23.toLocaleString("en-US")}</td>
                      <td>{row.y24.toLocaleString("en-US")}</td>
                      <td className="font-semibold text-green-700">
                        {row.diff.toLocaleString("en-US")}
                      </td>
                      <td>{row.pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[800px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable15;
